import BigNumber from 'bignumber.js';
import {
  FixedAssetDraft,
  FixedAssetPayload,
  FixedAssetResource,
  FixedAssetUpdatePayload,
  VATEntryAmounts,
} from '~/types/fixed-asset';
import { Money } from '~/types/money';

const HALF_UP = BigNumber.ROUND_HALF_UP;

const money = (value: BigNumber, currency: string): Money => ({ amount: value.toFixed(2), currency });

/** Serializes a 0-1 rate fraction to the string the backend expects, or undefined when missing. */
const rateToString = (rate?: number): string | undefined => (rate == null ? undefined : new BigNumber(rate).toString());

/**
 * Splits `total` across the items proportionally to `weights`, rounding each share to 2 decimals
 * via cumulative rounding so that the shares sum back exactly to `total`. When every weight is 0
 * the split is even.
 */
const depreciation = (total: BigNumber, weights: BigNumber[]): BigNumber[] => {
  const totalWeight = weights.reduce((acc, w) => acc.plus(w), new BigNumber(0));
  const shares: BigNumber[] = [];
  let previousCumulative = new BigNumber(0);
  let cumulativeWeight = new BigNumber(0);

  weights.forEach((weight, index) => {
    cumulativeWeight = cumulativeWeight.plus(weight);
    const cumulativeFraction = totalWeight.isZero()
      ? new BigNumber(index + 1).div(weights.length)
      : cumulativeWeight.div(totalWeight);
    const cumulative = total.times(cumulativeFraction).decimalPlaces(2, HALF_UP);
    shares.push(cumulative.minus(previousCumulative));
    previousCumulative = cumulative;
  });

  return shares;
};

/**
 * Maps the fixed asset drafts attached to a single VAT entry to the backend `fixed-asset` payload.
 *
 * The entry's `taxableAmount` (imponibile) and `taxAmount` (IVA) are apportioned across the drafts
 * using each draft's `originalBookValue` ("Valore cespite") as weight — book value is proportional
 * to the imponibile within one entry, since all assets share the same VAT rate and deductibility.
 * The non-deductible VAT share is capitalized into `initialBookValue`, matching the spec:
 * `initial_book_value = source_taxable_amount + vat_non_deductible_amount`.
 */
export const buildFixedAssetsPayload = (drafts: FixedAssetDraft[], entry: VATEntryAmounts): FixedAssetPayload[] => {
  if (drafts.length === 0) return [];

  const { currency } = entry.taxableAmount;
  const weights = drafts.map((draft) => new BigNumber(draft.originalBookValue ?? 0));
  const sourceTaxables = depreciation(new BigNumber(entry.taxableAmount.amount), weights);
  const vatPaids = depreciation(new BigNumber(entry.taxAmount.amount), weights);
  const deductibility = new BigNumber(entry.vatDeductibility ?? 100).div(100);

  return drafts.map((draft, index) => {
    const sourceTaxable = sourceTaxables[index] ?? new BigNumber(0);
    const vatPaid = vatPaids[index] ?? new BigNumber(0);
    const vatDeductible = vatPaid.times(deductibility).decimalPlaces(2, HALF_UP);
    const vatNonDeductible = vatPaid.minus(vatDeductible);
    const initialBookValue = sourceTaxable.plus(vatNonDeductible);

    const cap = draft.taxDeductibilityCap != null ? new BigNumber(draft.taxDeductibilityCap) : null;

    return {
      speciesCategoryId: draft.speciesCategoryId,
      description: draft.description,
      acquisitionDate: draft.acquisitionDate,
      serviceStartDate: draft.serviceStartDate,
      sourceTaxableAmount: money(sourceTaxable, currency),
      vatPaidTotal: money(vatPaid, currency),
      vatDeductibleAmount: money(vatDeductible, currency),
      vatNonDeductibleAmount: money(vatNonDeductible, currency),
      initialBookValue: money(initialBookValue, currency),
      acquisitionType: draft.acquisitionType,
      bookDepreciationRate: rateToString(draft.bookDepreciationRate),
      taxDepreciationRate: rateToString(draft.taxDepreciationRate),
      taxDeductibilityType: draft.taxDeductibilityType,
      taxDeductibilityRate: rateToString(draft.taxDeductibilityRate),
      taxDeductibilityCap: cap != null ? money(cap, currency) : null,
      status: 'IN_USE',
      assetType: draft.assetType,
      depreciationType: draft.depreciationType,
      referenceDepreciationRate: rateToString(draft.referenceDepreciationRate),
      notes: draft.notes,
    };
  });
};

const toNumber = (value?: string | number | null): number | undefined => {
  if (value == null) return undefined;
  const parsed = new BigNumber(value);
  return parsed.isFinite() ? parsed.toNumber() : undefined;
};

const moneyToNumber = (value?: Money | null): number | undefined => toNumber(value?.amount);

export const buildFixedAssetUpdatePayload = (draft: FixedAssetDraft, currency: string): FixedAssetUpdatePayload => {
  const bookValue = draft.originalBookValue != null ? new BigNumber(draft.originalBookValue) : null;
  const cap = draft.taxDeductibilityCap != null ? new BigNumber(draft.taxDeductibilityCap) : null;

  return {
    description: draft.description,
    serviceStartDate: draft.serviceStartDate,
    acquisitionType: draft.acquisitionType,
    initialBookValue: bookValue != null ? money(bookValue, currency) : undefined,
    bookDepreciationRate: rateToString(draft.bookDepreciationRate),
    taxDepreciationRate: rateToString(draft.taxDepreciationRate),
    taxDeductibilityType: draft.taxDeductibilityType,
    taxDeductibilityRate: rateToString(draft.taxDeductibilityRate),
    taxDeductibilityCap: cap != null ? money(cap, currency) : null,
    notes: draft.notes,
  };
};

export const mapFixedAssetToDraft = (asset: FixedAssetResource): FixedAssetDraft => ({
  description: asset.description ?? undefined,
  speciesCategoryId: asset.speciesCategory?.id,
  assetType: asset.assetType ?? undefined,
  depreciationType: asset.depreciationType ?? undefined,
  referenceDepreciationRate: toNumber(asset.referenceDepreciationRate),
  acquisitionDate: asset.acquisitionDate ?? undefined,
  serviceStartDate: asset.serviceStartDate ?? undefined,
  originalBookValue: moneyToNumber(asset.initialBookValue),
  acquisitionType: asset.acquisitionType ?? undefined,
  bookDepreciationRate: toNumber(asset.bookDepreciationRate),
  taxDepreciationRate: toNumber(asset.taxDepreciationRate),
  taxDeductibilityType: asset.taxDeductibilityType ?? undefined,
  taxDeductibilityRate: toNumber(asset.taxDeductibilityRate),
  taxDeductibilityCap: moneyToNumber(asset.taxDeductibilityCap) ?? null,
  notes: asset.notes ?? null,
});
